import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Web push subscription. Asks the user once on first authenticated visit; if granted,
 * registers via PushManager and ships the subscription envelope to the API.
 *
 * Quiet by design: a denied or dismissed prompt does nothing — there is no nag, no
 * persistent banner. The user can re-trigger from settings (Phase 8 polish).
 */
@Injectable({ providedIn: 'root' })
export class PushService {
  private readonly http = inject(HttpClient);
  private readonly swPush = inject(SwPush);
  private readonly base = `${environment.apiBaseUrl.replace(/\/+$/, '')}/api/push`;

  readonly status = signal<'unknown' | 'unsupported' | 'denied' | 'default' | 'granted'>('unknown');

  initialStatus(): void {
    if (!this.swPush.isEnabled || typeof Notification === 'undefined') {
      this.status.set('unsupported');
      return;
    }
    this.status.set(Notification.permission as 'default' | 'granted' | 'denied');
  }

  async ensureSubscribed(): Promise<void> {
    this.initialStatus();
    if (this.status() !== 'default' && this.status() !== 'granted') return;

    try {
      // Fetch the VAPID public key (anonymous endpoint).
      const key = await firstValueFrom(
        this.http.get<{ publicKey: string }>(`${this.base}/public-key`));
      if (!key?.publicKey) return;

      const sub = await this.swPush.requestSubscription({
        serverPublicKey: key.publicKey,
      });
      const json = sub.toJSON() as PushSubscriptionJSON;

      const p256dh = json.keys?.['p256dh'] ?? '';
      const auth = json.keys?.['auth'] ?? '';
      if (!json.endpoint || !p256dh || !auth) return;

      await firstValueFrom(this.http.post(`${this.base}/subscribe`, {
        endpoint: json.endpoint,
        p256DhKey: p256dh,
        authSecret: auth,
        userAgent: navigator.userAgent,
      }));
      this.status.set('granted');
    } catch {
      // User denied, or browser doesn't support — no-op. Per the brief, no nag.
    }
  }
}
